import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Answer } from '@/types/workerInfo.types';
import { colors, spacing } from '@/theme';
import { Check, Hash, AlertCircle } from 'lucide-react';

interface NumberInputProps {
  answer: Answer;
  onBlur: (value: { numberValue: number }) => void;
  disabled?: boolean;
}

export function NumberInput({ answer, onBlur, disabled }: NumberInputProps) {
  const [value, setValue] = useState<string>(answer.numberValue?.toString() || '');
  const [isFocused, setIsFocused] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const validationRules = answer.question.validationRules;
  const min = validationRules?.min ?? 0;
  const max = validationRules?.max ?? 100;

  useEffect(() => {
    setValue(answer.numberValue?.toString() || '');
  }, [answer.numberValue]);

  const validateNumber = (num: number): boolean => {
    if (isNaN(num)) {
      setError('Debe ingresar un número válido');
      return false;
    }
    if (num < min) {
      setError(`El valor mínimo es ${min}`);
      return false;
    }
    if (num > max) {
      setError(`El valor máximo es ${max}`);
      return false;
    }
    setError(null);
    return true;
  };

  const handleBlur = () => {
    setIsFocused(false);
    
    if (value === '') {
      if (answer.question.isRequired) {
        setError('Este campo es requerido');
      }
      return;
    }

    const numValue = parseFloat(value);
    if (validateNumber(numValue) && numValue !== parseFloat(answer.numberValue?.toString() || '0')) {
      onBlur({ numberValue: numValue });
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 2000);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      inputRef.current?.blur();
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: spacing[2],
        }}
      >
        <div>
          <label
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: colors.gray[900],
              display: 'flex',
              alignItems: 'center',
              gap: spacing[2],
              marginBottom: spacing[1],
            }}
          >
            <Hash size={16} color={colors.primary[500]} />
            {answer.question.question}
            {answer.question.isRequired && (
              <span style={{ color: colors.error?.[500] || '#ef4444', marginLeft: 4 }}>*</span>
            )}
          </label>
          {answer.question.description && (
            <p style={{ fontSize: 14, color: colors.gray[600], marginBottom: spacing[2] }}>
              {answer.question.description}
            </p>
          )}
        </div>

        <AnimatePresence>
          {isSaved && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing[1],
                color: colors.success?.[500] || '#10b981',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              <Check size={16} />
              <span>Guardado</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="number"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          style={{
            width: '100%',
            padding: spacing[4],
            paddingRight: spacing[8],
            borderRadius: 16,
            border: `2px solid ${
              error 
                ? colors.error?.[500] || '#ef4444'
                : isFocused 
                  ? colors.primary[500] 
                  : colors.gray[200]
            }`,
            fontSize: 15,
            fontFamily: 'inherit',
            outline: 'none',
            transition: 'all 0.2s ease',
            backgroundColor: isFocused ? colors.white : colors.gray[50],
            color: colors.gray[900],
          }}
          placeholder="Ingresa un número..."
          min={min}
          max={max}
          step="any"
        />
        
        {value && !isFocused && !error && (
          <div
            style={{
              position: 'absolute',
              right: spacing[3],
              top: '50%',
              transform: 'translateY(-50%)',
              color: colors.gray[400],
            }}
          >
            <Hash size={16} />
          </div>
        )}

        {error && (
          <div
            style={{
              position: 'absolute',
              right: spacing[3],
              top: '50%',
              transform: 'translateY(-50%)',
              color: colors.error?.[500] || '#ef4444',
            }}
          >
            <AlertCircle size={18} />
          </div>
        )}
      </div>

      {error && (
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            fontSize: 12,
            color: colors.error?.[500] || '#ef4444',
            marginTop: spacing[2],
            marginLeft: spacing[1],
          }}
        >
          {error}
        </motion.p>
      )}

      {validationRules && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: spacing[2],
            fontSize: 12,
            color: colors.gray[500],
          }}
        >
          <span>Mín: {min}</span>
          <span>Máx: {max}</span>
        </div>
      )}
    </div>
  );
}